import { useState, useRef } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useUploadLegacyJSA } from '@/hooks/use-safety';
import { useProjects } from '@/hooks/use-projects';
import { useCFLocations } from '@/hooks/use-cf-locations';

export function UploadLegacyJSADialog() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadLegacyJSA();
  const { data: projectsData } = useProjects({ page_size: 200 });
  const { data: locationsData } = useCFLocations({ page_size: 200 });

  const projects = projectsData?.results || [];
  const locations = locationsData?.results || [];

  const resetForm = () => {
    setTitle('');
    setProjectId('');
    setLocationId('');
    setDate('');
    setNotes('');
    setFile(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    setError('');

    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!file) {
      setError('File is required.');
      return;
    }

    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('file', file);
    formData.append('file_name', file.name);
    if (projectId) formData.append('project', projectId);
    if (locationId) formData.append('location_fk', locationId);
    if (date) formData.append('date', date);
    if (notes.trim()) formData.append('notes', notes.trim());

    try {
      await uploadMutation.mutateAsync(formData);
      resetForm();
      setOpen(false);
    } catch {
      setError('Upload failed. Please check the file and try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Upload Legacy JSA
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Legacy JSA</DialogTitle>
          <DialogDescription>
            Attach a scanned PDF or image of a historical JSA. This will be marked as a legacy upload.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 rounded p-2">{error}</div>
          )}

          <div className="space-y-1">
            <Label htmlFor="legacy-title">Title *</Label>
            <Input
              id="legacy-title"
              placeholder="e.g., Crane Lift JSA - Jan 2023"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="legacy-file">File (PDF, JPEG, or PNG) *</Label>
            <Input
              id="legacy-file"
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="space-y-1">
            <Label>Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a project (optional)" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={String(project.id)}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Location</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a location (optional)" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={String(loc.id)}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="legacy-date">Original Date</Label>
            <Input
              id="legacy-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="legacy-notes">Notes</Label>
            <Input
              id="legacy-notes"
              placeholder="Additional notes about this legacy JSA"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={uploadMutation.isPending}>
            {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
